import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { Button, Field, Notice, useUnsavedChanges } from "../components/ui";
import { confirmationReconciliationKey, reconcileConfirmationTypes } from "./confirmation-reconciliation";
import { ProjectSection, UnsavedProjectSection } from "./ProjectSection";

const makeKey = () =>
  `mandates-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;

export function ProjectForm({ project = {}, employees = [], conversion, onCancel, onSaved }) {
  const creating = !project.id;
  const initial = useMemo(
    () => ({
      name: project.name || "",
      abacusNr: project.abacusNr || "",
      uid: project.uid || "",
      gruppe: project.gruppe || "",
      mandatsleitungId: project.mandatsleitungId || "",
      budget: project.budget ?? "",
      intern: project.intern === true,
      confirmationRequired: false,
      bestaetigungsartIds: [],
    }),
    [project],
  );
  const [form, setForm] = useState(initial);
  const [baseline, setBaseline] = useState(initial);
  const [errors, setErrors] = useState({});
  const [request, setRequest] = useState({ status: "idle", message: "" });
  const [confirmationTypes, setConfirmationTypes] = useState([]);
  const createKey = useRef(makeKey());
  const confirmationKey = useRef(confirmationReconciliationKey());
  const pendingMandate = useRef(null);
  useEffect(() => {
    let active = true;
    Promise.all([
      api("/reference-data/bestaetigungsarten?limit=100"),
      project.id ? api(`/confirmations?mandatId=${project.id}&limit=1000`) : Promise.resolve({ items: [] }),
    ]).then(([typeData, confirmationData]) => {
      if (!active) return;
      const ids = confirmationData.items.map((item) => item.bestaetigungsartId);
      setConfirmationTypes(typeData.items.filter((item) => item.aktiv !== false));
      const selection = { confirmationRequired: ids.length > 0, bestaetigungsartIds: ids };
      setForm((current) => ({ ...current, ...selection }));
      setBaseline((current) => ({ ...current, ...selection }));
    }).catch((error) => active && setRequest({ status: "load-error", message: error.message }));
    return () => { active = false; };
  }, [project.id]);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const cancel = useUnsavedChanges(dirty, onCancel);
  const change = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };
  const toggleConfirmation = (id, checked) => setForm((current) => ({
    ...current,
    bestaetigungsartIds: checked ? [...current.bestaetigungsartIds, id] : current.bestaetigungsartIds.filter((value) => value !== id),
  }));
  const submit = async (event) => {
    event?.preventDefault();
    const local = {};
    if (!form.name.trim()) local.name = "Projektname ist erforderlich.";
    if (!form.abacusNr.trim())
      local.abacusNr = "Projekt-ID (Abacus) ist erforderlich.";
    if (!form.mandatsleitungId) local.mandatsleitungId = "Mandatsleitung ist erforderlich.";
    setErrors(local);
    if (Object.keys(local).length) return;
    const body = {
      name: form.name.trim(),
      uid: form.uid.trim() || null,
      gruppe: form.gruppe.trim() || null,
      mandatsleitungId: form.mandatsleitungId,
      budget: form.budget === "" ? null : Number(form.budget),
      intern: form.intern,
      abacusNr: form.abacusNr.trim(),
      ...(creating
        ? { ...(conversion ? {} : { status: "aktiv" }) }
        : { version: project.version }),
    };
    setRequest({ status: "saving", message: "" });
    try {
      let saved = pendingMandate.current;
      if (!saved) {
        if (conversion) {
          const convertedOffer = await api(`/offers/${conversion.offerId}/convert`, {
            method: "POST",
            headers: { "Idempotency-Key": createKey.current },
            retry: 1,
            body: JSON.stringify(body),
            successMessage: "Offerte als Projekt angelegt.",
          });
          saved = await api(`/mandates/${convertedOffer.mandatId}`);
        } else {
          saved = await api(creating ? "/mandates" : `/mandates/${project.id}`, {
            method: creating ? "POST" : "PATCH",
            ...(creating ? { headers: { "Idempotency-Key": createKey.current }, retry: 1 } : {}),
            body: JSON.stringify(body),
          });
        }
      }
      pendingMandate.current = saved;
      await reconcileConfirmationTypes({
        mandateId: saved.id || project.id,
        version: saved.version,
        bestaetigungsartIds: form.confirmationRequired ? form.bestaetigungsartIds : [],
        idempotencyKey: confirmationKey.current,
      });
      // A mutation is only presented as successful after the scoped read model agrees.
      const confirmed = await api(`/mandates/${saved.id || project.id}`);
      pendingMandate.current = null;
      onSaved(confirmed);
    } catch (error) {
      setRequest({
        status: pendingMandate.current ? "confirmation-error" : error.status === 403 ? "forbidden" : error.status === 409 ? "conflict" : "error",
        message: error.message,
      });
    }
  };
  return (
    <form className="project-dialog-layout project-master-form" onSubmit={submit} noValidate>
      <ProjectSection title="Projektübersicht">
      {conversion && (
        <div className="project-fixed-context">
          <span>Offertvolumen (CHF)</span>
          <strong>{conversion.betragVon}–{conversion.betragBis}</strong>
        </div>
      )}
      <Field id="project-abacus" label="Projekt-ID (Abacus)" error={errors.abacusNr}><input value={form.abacusNr} onChange={change("abacusNr")} /></Field>
      <Field id="project-name" label="Name" error={errors.name}><input autoFocus value={form.name} onChange={change("name")} /></Field>
      <div className="project-form-pair">
        <Field id="project-uid" label="UID-Nr."><input value={form.uid} onChange={change("uid")} /></Field>
        <Field id="project-group" label="Gruppe" error={errors.gruppe}><input value={form.gruppe} onChange={change("gruppe")} /></Field>
      </div>
      <Field id="project-leader" label="Mandatsleitung" error={errors.mandatsleitungId}>
        <select value={form.mandatsleitungId} onChange={change("mandatsleitungId")}>
          <option value="">Bitte wählen</option>
          {employees.filter((employee) => employee.aktiv !== false).map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
        </select>
      </Field>
      <Field id="project-budget" label="Budget (h)" error={errors.budget}><input type="number" min="0" step="5" value={form.budget} onChange={change("budget")} /></Field>
      <label className="checkbox-line"><input type="checkbox" checked={form.intern} onChange={(event) => setForm((current) => ({ ...current, intern: event.target.checked }))} /> <span>Internes Projekt</span></label>
      <p className="project-derived-hint"><strong>Phase(n) und Disposition</strong> werden aus der Planung abgeleitet und können hier nicht bearbeitet werden.</p>
      </ProjectSection>
      <UnsavedProjectSection title="Live aus der Planung" />
      <UnsavedProjectSection title="Kommentar zum Projekt" />
      <ProjectSection title="Bestätigungen">
      <fieldset className="confirmation-type-selection">
        <legend className="visually-hidden">Bestätigungen</legend>
        <label className="confirmation-required"><input type="checkbox" checked={form.confirmationRequired} onChange={(event) => setForm((current) => ({ ...current, confirmationRequired: event.target.checked }))} /> Drittbestätigung benötigt</label>
        {form.confirmationRequired && <div className="confirmation-type-options">{confirmationTypes.map((type) => <label key={type.id}><input type="checkbox" checked={form.bestaetigungsartIds.includes(type.id)} onChange={(event) => toggleConfirmation(type.id, event.target.checked)} /> {type.name}</label>)}</div>}
      </fieldset>
      </ProjectSection>
      <UnsavedProjectSection title="Zeitplan (Deadlines)" />
      <UnsavedProjectSection title="Dokumente (SharePoint)" />
      {request.status !== "idle" && request.status !== "saving" && (
        <Notice tone="error"><strong>{request.status === "confirmation-error" ? "Projekt gespeichert, Bestätigungsauswahl noch nicht bestätigt." : request.status === "load-error" ? "Bestätigungsauswahl konnte nicht geladen werden." : request.status === "forbidden" ? "Keine Berechtigung." : request.status === "conflict" ? "Versionskonflikt. Projekt neu laden und erneut bearbeiten." : "Speichern oder Bestätigen fehlgeschlagen."}</strong> {request.message} <Button type="button" variant="secondary" onClick={submit}>Erneut versuchen</Button></Notice>
      )}
      <div className="form-actions">
        <Button disabled={request.status === "saving" || (!creating && !dirty)}>{request.status === "saving" ? "Wird gespeichert …" : "Speichern"}</Button>
        <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
      </div>
    </form>
  );
}
